import type { ChatMessage } from "./ws-context";

/**
 * Content-identity key for a message. Used only to suppress an optimistic
 * (pending) message that the authoritative history has since absorbed — never
 * to order or to resurrect. Two messages with the same role+content (or same
 * widget kind+payload) are considered the same surface for that purpose.
 */
export const messageKey = (message: ChatMessage): string =>
  message.role === "widget"
    ? `widget ${message.widget.kind} ${JSON.stringify(message.widget.payload)}`
    // Include the author so two group-chat participants sending identical text
    // ("ok") aren't collapsed into one surface during reconcile.
    : `${message.role} ${message.author?.id ?? ""} ${message.content}`;

/**
 * Reconcile a fresh server `history` dump against the current local list.
 *
 * History is the source of truth: it arrives in chronological order and every
 * row carries a server `id`. The gateway only sends the newest 30 rows, so a
 * naive "append anything local that isn't in history" merge resurrects older
 * messages that have scrolled out of that window to the *bottom* of the list —
 * the cross-conversation mixing / wrong-order bug.
 *
 * The only messages worth carrying across a history refresh are genuinely
 * in-flight optimistic ones: the user message we just rendered locally and the
 * assistant turn we just streamed, neither of which the server snapshot may
 * include yet. Those are flagged `pending` (no server id) and are newer than
 * everything in history. So we keep exactly the `pending` messages that the
 * incoming history does not already cover, and drop everything else — a
 * confirmed message (has an id) is never re-appended, which is what kills the
 * resurrection.
 */
/** Pending (optimistic) rows older than this are treated as abandoned rather
 *  than in-flight and dropped on the next history refresh. Client-clock only:
 *  an optimistic row's createdAt is client time, so it must never be compared
 *  against server timestamps — phone clocks routinely sit seconds off the
 *  server, and a cross-clock comparison silently dropped just-sent messages
 *  on mobile (the bubble vanished until the next refresh confirmed the row). */
export const PENDING_TTL_MS = 5 * 60_000;

export const reconcileHistory = (
  history: ChatMessage[],
  local: ChatMessage[],
  now: number = Date.now(),
): ChatMessage[] => {
  if (local.length === 0) return history;

  // A pending optimistic row is "absorbed" only by a FRESHLY-confirmed history
  // row — one whose id the client isn't already holding. A same-content history
  // row we already have (same id) is a DISTINCT, older message, not the
  // confirmation of the just-sent one, so it must not suppress it. Without this
  // (keying suppression on bare content presence), re-sending a message whose
  // text matches a recent one — a common "ok" / "yes" / "?" or a retry — made
  // the just-sent bubble vanish on the next mid-turn reconnect dump (which
  // still shows only the earlier identical row) and not reappear until the
  // reply's dump finally carried the new row. It looked like the message was
  // eaten; it was only being deduped against its own older twin.
  const knownIds = new Set(local.map((m) => m.id).filter(Boolean));
  const absorbingKeys = new Set(
    history.filter((m) => !m.id || !knownIds.has(m.id)).map(messageKey),
  );

  const pending = local.filter(
    (m) =>
      m.pending === true &&
      !absorbingKeys.has(messageKey(m)) &&
      now - new Date(m.createdAt ?? 0).getTime() < PENDING_TTL_MS,
  );

  return [...history, ...pending];
};

/**
 * Prepend an older history page (scroll-up pagination) to the current list.
 *
 * The page is older than everything currently held and arrives in chronological
 * order, so it goes in front. Both sides carry server ids; dedup by id guards
 * against an overlapping row if a boundary is ever re-fetched.
 */
export const prependHistoryPage = (
  current: ChatMessage[],
  olderPage: ChatMessage[],
): ChatMessage[] => {
  if (olderPage.length === 0) return current;
  const seen = new Set(current.map((m) => m.id).filter(Boolean));
  const fresh = olderPage.filter((m) => !m.id || !seen.has(m.id));
  return [...fresh, ...current];
};

/**
 * Add a message the server SENT to this page (a scheduled or proactive
 * delivery) — unless the page already has it. It can: a delivery may be
 * buffered for minutes after its row is written, and a page opened in that
 * window loads the row from history first. The delivery carries the row's id
 * (older runtimes sent none, and those are always appended).
 */
export const appendDelivered = (prev: readonly ChatMessage[], message: ChatMessage): ChatMessage[] =>
  message.id !== undefined && prev.some((m) => m.id === message.id) ? [...prev] : [...prev, message];
